import { useEffect, useState } from "react";
import { X, Coins, Gift, Heart, Loader2, Check, Lock, ShieldCheck } from "lucide-react";
import { redeemCredits, fetchPricing } from "../lib/api";

const REASON_COPY = {
  account_suspended: "Account flagged — contact support.",
  gift_card_us_only: "Gift cards are US-only.",
  kyc_required_above_25: "You've redeemed $25+ — verify your identity to continue.",
  need_5000_vested_credits: "Need 5,000 vested credits (credits ripen after 14 days).",
  need_9990_vested_credits: "Need 9,990 vested credits.",
  need_2500_vested_credits: "Need 2,500 vested credits to unlock a benefit.",
};

export default function RedeemModal({ email, credits, onClose, onDone }) {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [charity, setCharity] = useState("");

  useEffect(() => {
    fetchPricing().then(setPricing).catch(() => {});
  }, [email]);

  const balance = credits?.balance ?? 0;
  const vested = credits?.vested ?? balance;
  const pending = credits?.pending ?? 0;
  const usd = credits?.usd_value ?? 0;
  const country = credits?.country || "US";
  const paths = credits?.redemption_paths || {};
  const proMonth = paths?.pro_month?.min_credits ?? 9990;
  const minGift = paths?.gift_card?.min_credits ?? 5000;
  const charities = pricing?.redemption?.charities || [];

  const submit = async (method) => {
    setMsg(null);
    setBusy(method);
    try {
      const payload = { email, method };
      if (method === "charity") payload.charity_id = charity;
      const r = await redeemCredits(payload);
      setMsg({ ok: true, text: `Redeemed via ${method.replace("_", " ")}.` });
      onDone?.(r);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Redemption failed." });
    } finally { setBusy(""); }
  };

  const Tile = ({ id, icon: Icon, title, sub, cost, action, gate, badge, testid }) => {
    const locked = !gate?.open;
    const reason = locked ? (REASON_COPY[gate?.reason] || gate?.reason) : null;
    return (
      <div className={`rounded-xl border p-4 ${locked ? "border-[var(--line)] bg-[#f7f7f4] opacity-80" : "border-[var(--line)] bg-white"}`} data-testid={testid}>
        <div className="flex items-start gap-3">
          <Icon size={20} className="text-[var(--blue)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-black text-[15px]">{title}</div>
              {badge && <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)] bg-[#fff7d6] px-2 py-0.5 rounded">{badge}</span>}
            </div>
            <p className="text-[12.5px] text-[var(--muted)] leading-snug mt-1">{sub}</p>
            <div className="text-[11px] text-[var(--muted)] mt-2">Cost · <b className="text-[var(--ink)]">{cost.toLocaleString()} credits</b></div>
            {locked && reason && (
              <div className="text-[11px] text-[var(--red,#b91c1c)] mt-1 flex items-center gap-1" data-testid={`${testid}-reason`}><Lock size={10} /> {reason}</div>
            )}
            <button onClick={action} disabled={!!busy || locked} className={`btn ${locked ? "" : "primary"} mt-3 w-full justify-center`} data-testid={`${testid}-btn`}>
              {busy === id ? <Loader2 size={14} className="animate-spin" /> : locked ? "Locked" : "Redeem"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Benefit-unlock pseudo-gate (lower threshold than gift cards). Re-uses
  // redemption rules but with a 2500 credit floor; not US-gated.
  const benefitGate = {
    open: (credits?.fraud_flag || "ok") !== "suspended" && vested >= 2500,
    reason: vested < 2500 ? "need_2500_vested_credits" : null,
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid="redeem-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[820px] max-h-[90vh] overflow-auto border border-[var(--line)]">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)] flex items-center gap-1"><Coins size={12} /> Redeem credits</div>
            <h2 className="text-[20px] font-black tracking-[-0.02em]">Vested · ${(vested * 0.001).toFixed(2)} <span className="text-[var(--muted)] font-bold text-[14px]">({vested.toLocaleString()} credits)</span></h2>
            {pending > 0 && (
              <div className="text-[11px] text-[var(--muted)] mt-0.5" data-testid="redeem-pending-line">
                {pending.toLocaleString()} pending credits ripen in ≤14 days · Total balance ${usd.toFixed(2)}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 -mr-2"><X /></button>
        </div>

        <div className="px-5 pt-4">
          <div className="rounded-lg bg-[var(--sky)] border border-[var(--sky-2)] px-3 py-2 text-[11.5px] text-[var(--ink)] flex items-start gap-2" data-testid="redeem-safety-strip">
            <ShieldCheck size={14} className="text-[var(--blue)] shrink-0 mt-0.5" />
            <div>
              <b>Safe by design.</b> Credits convert to Pro months, member benefits, gift cards (US), or charity. No cash. No sweepstakes. No fraud incentive.
              {country !== "US" && <span className="block mt-1">Your account is <b>{country}</b> — Pro months, benefits, and charity are open to you. Gift cards are US-only.</span>}
            </div>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Tile
            id="benefit_unlock" icon={ShieldCheck} testid="redeem-benefit-unlock"
            title="Unlock a Member Benefit" badge="Lower threshold"
            sub="Spend credits to unlock any premium partner deal from the 100+ Hub. Browse, then redeem."
            cost={2500}
            gate={benefitGate}
            action={() => submit("benefit_unlock")}
          />
          <Tile
            id="gift_card" icon={Gift} testid="redeem-gift-card"
            title="$5 Visa / Amazon gift card" badge="US only · 14-day vest"
            sub="Sent to your email by Tremendous. Pick brand at redemption."
            cost={minGift}
            gate={paths.gift_card || { open: false }}
            action={() => submit("gift_card")}
          />
          <div className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid="redeem-charity">
            <div className="flex items-start gap-3">
              <Heart size={20} className="text-[var(--red,#b91c1c)] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-black text-[15px]">Donate $5 to charity</div>
                <p className="text-[12.5px] text-[var(--muted)] leading-snug mt-1">100% goes to the nonprofit — Newseze covers the fees. Open to all countries.</p>
                <select value={charity} onChange={(e) => setCharity(e.target.value)} className="field w-full mt-2 text-[13px]" data-testid="redeem-charity-select">
                  <option value="">Pick a charity…</option>
                  {charities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="text-[11px] text-[var(--muted)] mt-2">Cost · <b className="text-[var(--ink)]">{minGift.toLocaleString()} credits</b></div>
                <button onClick={() => submit("charity")} disabled={!!busy || !charity || !paths?.charity?.open} className="btn primary mt-3 w-full justify-center" data-testid="redeem-charity-btn">
                  {busy === "charity" ? <Loader2 size={14} className="animate-spin" /> : "Donate $5"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {msg && (
          <div className={`px-5 pb-5 text-[13px] ${msg.ok ? "text-[var(--blue)]" : "text-[var(--red,#b91c1c)]"} flex items-center gap-2`} data-testid="redeem-msg">
            {msg.ok && <Check size={14} />} {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}
